/**
 * Helpers puros para importación de legajos con CUIT repetido en la planilla.
 * Cada fila es un crédito/legajo; un mismo CUIT identifica un solo cliente.
 */

export function normalizeCuit(cuit) {
  if (cuit == null) return '';
  return String(cuit).replace(/\D/g, '');
}

export function nombreClienteDeFila(fila) {
  if (!fila) return '';
  const razonSocial = fila.RazonSocial ? String(fila.RazonSocial).trim() : '';
  if (razonSocial) return razonSocial;
  const nombre = fila.Nombre ? String(fila.Nombre).trim() : '';
  const apellido = fila.Apellido ? String(fila.Apellido).trim() : '';
  return `${nombre} ${apellido}`.trim();
}

/**
 * Resume CUITs de las filas a importar.
 * @param {Array<{ CUIT?: string|number }>} filas
 * @returns {{
 *   nLegajos: number,
 *   nClientes: number,
 *   nClientesConVariosCreditos: number,
 *   clientesConVariosCreditos: Array<{ cuit: string, cliente: string, legajos: number }>,
 *   mensaje: string,
 * }}
 */
export function resumenCuitsEnFilas(filas = []) {
  const porCuit = new Map();
  for (const fila of filas) {
    const key = normalizeCuit(fila && fila.CUIT);
    if (!key) continue;
    const actual = porCuit.get(key);
    if (actual) {
      actual.legajos += 1;
      if (!actual.cliente) actual.cliente = nombreClienteDeFila(fila);
    } else {
      porCuit.set(key, { cuit: key, cliente: nombreClienteDeFila(fila), legajos: 1 });
    }
  }

  const clientesConVariosCreditos = Array.from(porCuit.values())
    .filter(function (c) {
      return c.legajos > 1;
    })
    .sort(function (a, b) {
      return b.legajos - a.legajos;
    });

  const nLegajos = filas.length;
  const nClientes = porCuit.size;
  const nClientesConVariosCreditos = clientesConVariosCreditos.length;

  const detalle = clientesConVariosCreditos
    .map(function (c) {
      return `• ${c.cliente || `CUIT ${c.cuit}`}: ${c.legajos} legajos`;
    })
    .join('\n');

  const mensaje =
    `Se van a crear ${nLegajos} legajos para ${nClientes} clientes.\n\n` +
    `${nClientesConVariosCreditos} ${
      nClientesConVariosCreditos === 1 ? 'cliente tiene' : 'clientes tienen'
    } más de un crédito en esta planilla:\n${detalle}`;

  return {
    nLegajos,
    nClientes,
    nClientesConVariosCreditos,
    clientesConVariosCreditos,
    mensaje,
  };
}

/**
 * Reutiliza cliente/usuario ya resuelto para el CUIT, o lo busca en el resultado de /clientes/search.
 * @param {string|number} cuit
 * @param {Map<string, object>} mapa
 * @param {Array} usuarios
 */
export function resolverClientePorCuit(cuit, mapa, usuarios) {
  const lista = usuarios || [];
  const key = normalizeCuit(cuit);
  if (key && mapa.has(key)) {
    return Object.assign({}, mapa.get(key));
  }

  const encontrado = lista.find(function (c) {
    return normalizeCuit(c.cuit) === key && c.usuario && c.usuario.active === true;
  });

  const datos =
    encontrado && encontrado.usuario
      ? {
          clienteId: encontrado.id,
          usuarioEmail: encontrado.usuario.email || '',
          usuarioId: encontrado.usuario.id,
          usuario: encontrado.usuario.username || '',
        }
      : {
          clienteId: '',
          usuarioEmail: '',
          usuarioId: '',
          usuario: '',
        };

  if (key) {
    mapa.set(key, datos);
  }

  return datos;
}
